import { Checkbox } from "@/components/ui/checkbox";
import { UserRound, FileText, Briefcase, Sparkles, GraduationCap, FolderKanban, Award } from "lucide-react";
import { Field, TextField, AutoTextarea, ChipInput, BulletList, arr, nameOf, uniq } from "./fields";
import { ListEditor } from "./ListEditor";

const listPatch = (patch, key, norm = (x) => x) => (fn) => patch((p) => ({ [key]: fn(arr(p[key]).map(norm)) }));
const join = (...xs) => xs.filter(Boolean).join(" · ");
const count = (n, one) => (n ? `${n} ${one}${n === 1 ? "" : "s"}` : "");
const certObj = (c) => (typeof c === "string" ? { name: c } : c);

export const splitPeriod = (e) => {
  if (e.start_date || e.end_date) return [e.start_date || "", e.end_date || ""];
  const parts = (e.period || e.duration || e.dates || "").split(/\s+(?:-|–|—|to)\s+/i);
  return parts.length === 2 ? parts : [parts[0] || "", ""];
};
const periodOf = (e) => e.period || [e.start_date, e.end_date].filter(Boolean).join(" - ");
const bulletsOf = (e) => (arr(e.responsibilities).length ? e.responsibilities : arr(e.achievements).length ? [] : arr(e.highlights)).map(String);
const schoolOf = (e) => e.institution || e.school || e.university || "";
const langText = (l) => (typeof l === "string" ? l : l?.proficiency ? `${l.name} (${l.proficiency})` : l?.name || "");
const langObj = (s) => { const m = s.match(/^(.*?)\s*\((.*)\)\s*$/); return m ? { name: m[1], proficiency: m[2] } : { name: s, proficiency: "" }; };

export function AboutEditor({ p, patch, requireEmail, emailOk }) {
  const c = p.contact || {};
  const l = p.links || {};
  const setC = (k) => (v) => patch((q) => ({ contact: { ...(q.contact || {}), [k]: v } }));
  const setL = (k) => (v) => patch((q) => ({ links: { ...(q.links || {}), [k]: v } }));
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name"><TextField value={c.name} onChange={setC("name")} testId="review-name" placeholder="Your name" /></Field>
        <Field label="Email" hint={requireEmail ? "you'll sign in with this" : ""}>
          <TextField type="email" value={c.email} onChange={setC("email")} testId="review-email" placeholder="you@email.com" aria-invalid={!emailOk} className={!emailOk && c.email ? "border-rose-500/60" : ""} />
          {requireEmail && !emailOk && <p className="text-xs text-rose-400" data-testid="review-email-error">{c.email ? "That email doesn't look right." : "Add your email to continue."}</p>}
        </Field>
        <Field label="Phone"><TextField value={c.phone} onChange={setC("phone")} testId="review-phone" placeholder="+968 …" /></Field>
        <Field label="Location"><TextField value={c.location} onChange={setC("location")} testId="review-location" placeholder="City, country" /></Field>
        <Field label="Headline" className="sm:col-span-2"><TextField value={p.headline} onChange={(v) => patch(() => ({ headline: v }))} testId="review-headline" placeholder="e.g. Senior SOC Analyst · Threat detection" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="LinkedIn" hint="optional"><TextField value={l.linkedin} onChange={setL("linkedin")} testId="review-link-linkedin" placeholder="linkedin.com/in/…" /></Field>
        <Field label="Portfolio or website" hint="optional"><TextField value={l.portfolio || l.website} onChange={setL("portfolio")} testId="review-link-portfolio" placeholder="https://…" /></Field>
      </div>
    </div>
  );
}

export function SummaryEditor({ p, patch }) {
  const n = (p.summary || "").length;
  return (
    <Field label="Professional summary" hint="2–3 sentences work best">
      <AutoTextarea value={p.summary} onChange={(v) => patch(() => ({ summary: v }))} testId="review-summary" placeholder="What you do, what you're great at, and what you're looking for." className="min-h-[96px]" />
      <div className="text-right text-[11px] text-muted-foreground" data-testid="review-summary-count">{n} characters</div>
    </Field>
  );
}

function RoleFields({ e, set, i }) {
  const [start, end] = splitPeriod(e);
  const current = /present|current|now/i.test(end);
  const dates = (s, en) => set({ start_date: s, end_date: en, period: [s, en].filter(Boolean).join(" - ") });
  const resp = bulletsOf(e);
  const ach = arr(e.achievements).map(String);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Job title"><TextField value={e.title || e.role} onChange={(v) => set({ title: v })} testId={`role-${i}-title`} placeholder="e.g. Security Engineer" /></Field>
        <Field label="Company"><TextField value={e.company || e.organization} onChange={(v) => set({ company: v })} testId={`role-${i}-company`} placeholder="Company name" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Location"><TextField value={e.location} onChange={(v) => set({ location: v })} testId={`role-${i}-location`} placeholder="City or Remote" /></Field>
        <Field label="Start"><TextField value={start} onChange={(v) => dates(v, end)} testId={`role-${i}-start`} placeholder="e.g. Jan 2021" /></Field>
        <Field label="End"><TextField value={current ? "Present" : end} disabled={current} onChange={(v) => dates(start, v)} testId={`role-${i}-end`} placeholder="e.g. Mar 2024" /></Field>
      </div>
      <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
        <Checkbox checked={current} onCheckedChange={(c) => dates(start, c ? "Present" : "")} data-testid={`role-${i}-current`} /> I currently work here
      </label>
      <Field label="Role summary" hint="optional"><AutoTextarea value={e.summary || e.description} onChange={(v) => set({ summary: v })} testId={`role-${i}-summary`} placeholder="One line about the role" /></Field>
      <Field label="Responsibilities" hint="Enter adds a new point"><BulletList value={resp} onChange={(v) => set({ responsibilities: v, highlights: [...v, ...ach] })} placeholder="What you were responsible for" testId={`role-${i}-resp`} /></Field>
      <Field label="Achievements" hint="results you're proud of"><BulletList value={ach} onChange={(v) => set({ achievements: v, highlights: [...resp, ...v] })} placeholder="e.g. Cut alert triage time by 40%" addLabel="Add an achievement" testId={`role-${i}-ach`} /></Field>
      <Field label="Technologies"><ChipInput value={uniq(arr(e.technologies).map(nameOf))} onChange={(v) => set({ technologies: v })} placeholder="Type a tool and press Enter" testId={`role-${i}-tech`} /></Field>
    </>
  );
}

export function ExperienceEditor({ p, patch }) {
  return (
    <ListEditor items={arr(p.experience)} onChange={listPatch(patch, "experience")} testId="review-experience" addLabel="Add a role" emptyText="No roles found in your CV yet."
      blank={{ title: "", company: "", location: "", start_date: "", end_date: "", period: "", summary: "", responsibilities: [], achievements: [], technologies: [], highlights: [] }}
      itemTitle={(e) => join(e.title || e.role || "Untitled role", e.company || e.organization)} itemMeta={(e) => join(periodOf(e), e.location)}
      renderFields={(e, set, i) => <RoleFields e={e} set={set} i={i} />} />
  );
}

export function SkillsEditor({ p, patch }) {
  const cats = arr(p.skill_categories).filter((c) => c && arr(c.skills).length);
  const inCats = new Set(cats.flatMap((c) => arr(c.skills).map((s) => nameOf(s).toLowerCase())));
  const other = uniq(arr(p.skills).map(nameOf)).filter((n) => !inCats.has(n.toLowerCase()));
  const isToolCat = (c) => /tool|platform/i.test(c.category || "");
  const hasToolCat = cats.some(isToolCat);
  const commit = (nextCats, nextOther) => {
    const kept = nextCats.filter((c) => c.skills.length);
    const toolCat = nextCats.find(isToolCat);
    patch(() => ({ skill_categories: kept, skills: uniq([...kept.flatMap((c) => c.skills.map(nameOf)), ...nextOther]), ...(toolCat ? { tools: uniq(toolCat.skills.map(nameOf)) } : {}) }));
  };
  const setCat = (ci, names) => {
    const byName = Object.fromEntries(arr(cats[ci].skills).map((s) => [nameOf(s).toLowerCase(), typeof s === "string" ? { name: s } : s]));
    commit(cats.map((c, idx) => (idx === ci ? { ...c, skills: names.map((n) => byName[n.toLowerCase()] || { name: n }) } : c)), other);
  };
  const levelOf = (c, n) => arr(c.skills).find((s) => nameOf(s).toLowerCase() === n.toLowerCase())?.proficiency;
  return (
    <div className="space-y-5">
      {cats.map((c, ci) => (
        <Field key={`${c.category}-${ci}`} label={c.category || "Skills"}>
          <ChipInput value={uniq(arr(c.skills).map(nameOf))} onChange={(v) => setCat(ci, v)} testId={`review-skillcat-${ci}`}
            renderChip={(n) => { const lv = levelOf(c, n); return <>{n}{lv && <span className="ml-1.5 text-[10px] font-medium text-primary/80">{lv}</span>}</>; }} />
        </Field>
      ))}
      <Field label={cats.length ? "Other skills" : "Skills"} hint="Enter or comma to add"><ChipInput value={other} onChange={(v) => commit(cats, v)} testId="review-skills" placeholder="e.g. Incident response" /></Field>
      {!hasToolCat && <Field label="Tools & platforms"><ChipInput value={uniq(arr(p.tools).map(nameOf))} onChange={(v) => patch(() => ({ tools: v }))} testId="review-tools" placeholder="e.g. Splunk" /></Field>}
      {cats.length > 0 && <p className="text-xs text-muted-foreground">Levels such as "Expert" are AI estimates from your CV.</p>}
    </div>
  );
}

export function EducationEditor({ p, patch }) {
  return (
    <div className="space-y-5">
      <ListEditor items={arr(p.education)} onChange={listPatch(patch, "education")} testId="review-education" addLabel="Add education" emptyText="No education found in your CV yet."
        blank={{ degree: "", field: "", institution: "", location: "", period: "" }}
        itemTitle={(e) => [e.degree || e.qualification || e.title || "Qualification", e.field || e.discipline].filter(Boolean).join(", ")}
        itemMeta={(e) => join(schoolOf(e), e.period || e.year || e.completion_date)}
        renderFields={(e, set, i) => (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Degree or qualification"><TextField value={e.degree || e.qualification || e.title} onChange={(v) => set({ degree: v })} testId={`edu-${i}-degree`} placeholder="e.g. BSc" /></Field>
            <Field label="Field of study"><TextField value={e.field || e.discipline} onChange={(v) => set({ field: v })} testId={`edu-${i}-field`} placeholder="e.g. Computer Science" /></Field>
            <Field label="School"><TextField value={schoolOf(e)} onChange={(v) => set({ institution: v })} testId={`edu-${i}-school`} placeholder="University or college" /></Field>
            <Field label="Dates"><TextField value={e.period || e.year || e.completion_date} onChange={(v) => set({ period: v })} testId={`edu-${i}-period`} placeholder="e.g. 2015 - 2019" /></Field>
          </div>
        )} />
      <Field label="Languages" hint='e.g. "Arabic (Native)"'>
        <ChipInput value={arr(p.languages).map(langText).filter(Boolean)} onChange={(v) => patch(() => ({ languages: v.map(langObj) }))} testId="review-languages" placeholder="Add a language" />
      </Field>
    </div>
  );
}

export function ProjectsEditor({ p, patch }) {
  return (
    <ListEditor items={arr(p.projects)} onChange={listPatch(patch, "projects")} testId="review-projects" addLabel="Add a project" emptyText="No projects found in your CV yet."
      blank={{ name: "", role: "", description: "", achievement: "", tech: [], capabilities: [] }}
      itemTitle={(pr) => pr.name || pr.title || "Untitled project"} itemMeta={(pr) => join(pr.role, pr.customer_industry)}
      renderFields={(pr, set, i) => (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Project name"><TextField value={pr.name || pr.title} onChange={(v) => set({ name: v })} testId={`project-${i}-name`} /></Field>
            <Field label="Your role"><TextField value={pr.role} onChange={(v) => set({ role: v })} testId={`project-${i}-role`} placeholder="e.g. Lead implementer" /></Field>
          </div>
          <Field label="What it was"><AutoTextarea value={pr.description || pr.summary} onChange={(v) => set({ description: v })} testId={`project-${i}-desc`} placeholder="One or two lines" /></Field>
          <Field label="Result" hint="optional"><TextField value={pr.achievement} onChange={(v) => set({ achievement: v })} testId={`project-${i}-result`} placeholder="e.g. Rolled out to 3,000 users" /></Field>
          <Field label="Technologies"><ChipInput value={uniq(arr(pr.tech).map(nameOf))} onChange={(v) => set({ tech: v })} testId={`project-${i}-tech`} /></Field>
        </>
      )} />
  );
}

export function CertsEditor({ p, patch }) {
  return (
    <ListEditor items={arr(p.certifications).map(certObj)} onChange={listPatch(patch, "certifications", certObj)} testId="review-certifications"
      addLabel="Add a certification" emptyText="No certifications found in your CV yet." blank={{ name: "", issuer: "", date: "", credential_id: "" }}
      itemTitle={(c) => c.name || c.title || "Untitled certification"} itemMeta={(c) => join(c.issuer, c.date)}
      renderFields={(c, set, i) => (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Certification"><TextField value={c.name || c.title} onChange={(v) => set({ name: v })} testId={`cert-${i}-name`} /></Field>
          <Field label="Issued by"><TextField value={c.issuer} onChange={(v) => set({ issuer: v })} testId={`cert-${i}-issuer`} placeholder="e.g. ISC2" /></Field>
          <Field label="Date"><TextField value={c.date} onChange={(v) => set({ date: v })} testId={`cert-${i}-date`} placeholder="e.g. 2023" /></Field>
          <Field label="Credential ID" hint="optional"><TextField value={c.credential_id} onChange={(v) => set({ credential_id: v })} testId={`cert-${i}-id`} /></Field>
        </div>
      )} />
  );
}

const trim = (s, n = 90) => (s && s.length > n ? `${s.slice(0, n)}…` : s || "");

export const SECTIONS = [
  { k: "about", title: "About you", icon: UserRound, conf: "contact", always: true, Editor: AboutEditor, summary: (p) => join(p.contact?.name, p.headline) || "Name, contact and headline" },
  { k: "summary", title: "Summary", icon: FileText, conf: "summary", always: true, Editor: SummaryEditor, summary: (p) => trim(p.summary) || "A short intro recruiters read first" },
  { k: "experience", title: "Experience", icon: Briefcase, conf: "experience", list: "experience", Editor: ExperienceEditor, summary: (p) => count(arr(p.experience).length, "role") },
  { k: "skills", title: "Skills & tools", icon: Sparkles, conf: "skills", always: true, Editor: SkillsEditor, summary: (p) => join(count(arr(p.skills).length, "skill"), count(arr(p.tools).length, "tool")) },
  { k: "education", title: "Education & languages", icon: GraduationCap, conf: "education", list: "education", Editor: EducationEditor, summary: (p) => join(count(arr(p.education).length, "qualification"), count(arr(p.languages).length, "language")) },
  { k: "projects", title: "Projects", icon: FolderKanban, conf: "projects", list: "projects", Editor: ProjectsEditor, summary: (p) => count(arr(p.projects).length, "project") },
  { k: "certifications", title: "Certifications", icon: Award, conf: "certifications", list: "certifications", Editor: CertsEditor, summary: (p) => count(arr(p.certifications).length, "certification") },
];
